import { action, computed, observable } from "mobx";
import { ContractPriceTemplateRecordInput } from "@config_editor/ContractsPriceTemplates/ContractsPriceTemplateFormInput";
import { uniq, uniqBy, groupBy, sortBy } from "lodash";
import { PayUnit, PriceListDay, PriceListWorkType } from "@bonliva-crm/shared";
import { PayPeriod } from "@bonliva-crm/graphql";
import { BC_contractsTypes } from "@consultants/ConsultantContract/utils";

export type recordType = ContractPriceTemplateRecordInput & {
  name: string; // formName to handleEdit
  index: string;
  indexInWorkType: number;
};

export type sortItem = {
  _id: string;
  workType: string;
  indexInWorkType?: number;
};

export type TRecordIdsByWorkType = { [key: string]: sortItem[] };

export class PriceRecordAltStore {
  constructor(public formStore: any, public name: string) {
    this.init();
  }

  init = () => {
    this.handleWorkTypes([]);
    this.initSortRecords();
  };

  @observable activeWorkTypes: PriceListWorkType[] = [];
  @observable selectedWorkType: PriceListWorkType = null;
  @observable showWorkTypeDialog = false;
  @observable showDayTypeDialog = false;
  @observable sortedRecordIds: TRecordIdsByWorkType = {};

  @computed get rawRecords() {
    return this.formStore.getValue(this.name);
  }

  @computed get records(): recordType[] {
    return this.rawRecords.map((x, i) => ({
      ...x,
      name: `${this.name}.${i}`,
      index: i
    }));
  }

  //todo replace this, it is a temp solution
  @computed get isPsychologist(): boolean {
    const professionsName = this.name.replace("records", "professions");
    return (
      this.formStore.getValue("Profession") === "Psykolog" ||
      this.formStore.getValue(professionsName)?.includes("Psykolog||||||")
    );
  }

  @computed get isDoctor(): boolean {
    const professionsName = this.name.replace("records", "professions");
    return (
      this.formStore.getValue("Profession") === "Läkare" ||
      this.formStore.getValue(professionsName)?.includes("Läkare||||||")
    );
  }

  @computed get getUniqWorkTypeFromRecords(): PriceListWorkType[] {
    return uniqBy(this.records, "workType").map((x) => x.workType);
  }

  @computed get allUniqWorkTypeFromRecords(): PriceListWorkType[] {
    return uniq([...this.getUniqWorkTypeFromRecords, ...this.activeWorkTypes]);
  }

  @computed get isBCContractTypeSelected(): boolean {
    return BC_contractsTypes.includes(this.formStore?.values?.ContractType);
  }

  @computed get sortItems() {
    return sortBy(
      this.records.map(({ _id, workType, indexInWorkType }) => ({
        _id,
        workType,
        indexInWorkType
      })),
      ["workType", "indexInWorkType"]
    );
  }

  @action initSortRecords() {
    this.sortedRecordIds = groupBy(this.sortItems, "workType");
  }

  @action resetSortRecords(records: ContractPriceTemplateRecordInput[]) {
    const sorted = sortBy(
      records
        .filter((x) => !x.deleted)
        .map(({ _id, workType, indexInWorkType }) => ({
          _id,
          workType,
          indexInWorkType
        })),
      ["workType", "indexInWorkType"]
    );
    this.sortedRecordIds = groupBy(sorted, "workType");
  }

  @action handleSortRecords = (workType: string, items: sortItem[]) => {
    this.sortedRecordIds[workType] = items;

    items.forEach((el, i) => {
      const rec = this.rawRecords.find((x) => x._id === el._id);
      rec.indexInWorkType = i;
    });

    this.formStore.setValue(this.name, this.rawRecords);
  };

  @action handleDelete = (workType: string, items: sortItem[], record: recordType) => {
    this.sortedRecordIds[workType] = items;
    this.formStore.setValue(`${record.name}.deleted`, !record.deleted);
  };

  @action toggleWorkTypeDialog = () => {
    this.showWorkTypeDialog = !this.showWorkTypeDialog;
  };

  @action toggleDayTypeDialog = (selectedWorkType: PriceListWorkType = null) => {
    const show = !this.showDayTypeDialog;
    this.showDayTypeDialog = show;
    this.selectedWorkType = show ? selectedWorkType : null;
  };

  @action handleWorkTypes = (value: PriceListWorkType[]) => {
    this.activeWorkTypes = uniq([...this.getUniqWorkTypeFromRecords, ...value]);
  };

  @action handleDayTypes = (values: string[]): ContractPriceTemplateRecordInput[] => {
    const res = [];
    values.forEach((v) => {
      const record = this.isBCContractTypeSelected
        ? ContractPriceTemplateRecordInput.CreateBlankBC()
        : ContractPriceTemplateRecordInput.CreateBlank();
      record.workType = this.selectedWorkType;
      if (v === PayPeriod.FullPeriod) {
        record.period = v as PayPeriod;
      } else if (Object.values(PriceListDay).includes(v as PriceListDay)) {
        record.startDay = v as PriceListDay;
      } else {
        record.startDay = null;
      }
      if (v === "freeInput") {
        record.isFreeInput = true;
      }

      if (
        [
          PriceListWorkType.NeuropsychologicalExaminationAdults,
          PriceListWorkType.PsychologicalInvestigationAdults,
          PriceListWorkType.PsychologicalInvestigationChildrenAndYoungPeople,
          PriceListWorkType.MedicalExaminationAdults,
          PriceListWorkType.MedicalExaminationChildrenAndYoungPeople
        ].includes(this.selectedWorkType)
      ) {
        record.paidPer = PayUnit.Investigation;
      }

      res.push(record);
    });
    const old = this.sortedRecordIds[this.selectedWorkType]
      ? this.sortedRecordIds[this.selectedWorkType]
      : [];
    const newSortedIds: sortItem[] = res.map((x) => ({
      _id: x._id as string,
      workType: x.workType as PriceListWorkType
    }));
    this.sortedRecordIds[this.selectedWorkType] = [...old, ...newSortedIds];
    return res;
  };
}
